import React, { useState } from 'react';
import { fileAPI } from '../../services/api';

const FilesTab = ({ person, updateField }) => {
  const [uploading, setUploading] = useState(false);
  const [newGroup, setNewGroup] = useState('');
  const [viewer, setViewer] = useState({ open: false, filename: '', content: '', saving: false, error: '' });

  const generalFiles = person.generalFiles || [];

  // Normalize shape: handles both old and new file formats
  const normalizedFiles = generalFiles.flatMap((entry) => {
    if (!entry) return [];
    
    // New format with attachedFiles array
    if (Array.isArray(entry.attachedFiles) && entry.attachedFiles.length > 0) {
      return entry.attachedFiles.map((p) => {
        const pathStr = (p || '').toString();
        const parts = pathStr.split(/[\\/]/).filter(Boolean);
        const filename = parts.length ? parts[parts.length - 1] : '';
        return {
          id: `${entry.id || filename}:${filename}`,
          name: filename,
          path: pathStr,
          size: entry.size,
          modified: entry.modified,
          group: entry.group || 'General',
        };
      });
    }
    
    // Standard format - return as is
    const pathStr = (entry.path || '').toString();
    const parts = pathStr.split(/[\\/]/).filter(Boolean);
    const filename = parts.length ? parts[parts.length - 1] : (entry.name || '');
    
    return [{
      id: entry.id || filename,
      name: filename || entry.name,
      path: pathStr || filename,
      size: entry.size,
      modified: entry.modified,
      group: entry.group || 'General',
    }];
  });

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await fileAPI.upload(file);
      const fileItem = {
        id: Date.now().toString(),
        name: file.name,
        path: response.data.stored_filename,
        size: file.size,
        modified: new Date().toISOString(),
        group: newGroup || 'General',
      };

      updateField('generalFiles', [...generalFiles, fileItem]);
      setNewGroup('');
    } catch (error) {
      console.error('File upload error:', error);
      alert('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = (id) => {
    updateField('generalFiles', generalFiles.filter(file => file.id !== id));
  };

  const openViewer = async (file) => {
    // Get the stored filename - prefer path, fall back to name
    let filename = file.path || file.name || '';
    
    // If it's a full path, extract just the filename
    if (filename.includes('/') || filename.includes('\\')) {
      const parts = filename.split(/[\\/]/).filter(Boolean);
      filename = parts[parts.length - 1];
    }
    
    // If still empty, use the file id (strip any suffix after colon)
    if (!filename && file.id) {
      filename = file.id.split(':')[0];
    }
    
    if (!filename) {
      alert('Cannot determine filename');
      return;
    }

    try {
      const res = await fileAPI.getContent(filename);
      setViewer({ open: true, filename, content: res.data.content || '', saving: false, error: '' });
    } catch (e) {
      // Try without extension if the file has one
      const dotIndex = filename.lastIndexOf('.');
      if (dotIndex > 0) {
        const baseName = filename.substring(0, dotIndex);
        try {
          const res = await fileAPI.getContent(baseName);
          setViewer({ open: true, filename: baseName, content: res.data.content || '', saving: false, error: '' });
          return;
        } catch {}
      }
      setViewer({ open: true, filename, content: '', saving: false, error: 'Preview not available for this file.' });
    }
  };

  const saveViewer = async () => {
    setViewer(v => ({ ...v, saving: true, error: '' }));
    try {
      await fileAPI.saveContent(viewer.filename, viewer.content);
      setViewer(v => ({ ...v, saving: false }));
    } catch (e) {
      setViewer(v => ({ ...v, saving: false, error: 'Save failed' }));
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const groupedFiles = normalizedFiles.reduce((acc, file) => {
    const group = file.group || 'General';
    if (!acc[group]) acc[group] = [];
    acc[group].push(file);
    return acc;
  }, {});

  const getDisplayName = (file) => {
    // Try various fields to get the best display name
    const pathStr = file.path || file.filePath || file.filename || file.fullPath || file.name || '';
    const parts = pathStr.toString().split(/[\\/]/).filter(Boolean);
    const lastToken = parts.length ? parts[parts.length - 1] : '';

    const preferred =
      file.originalName ||
      file.original_name ||
      file.clientName ||
      (lastToken && /[.][^.]+$/.test(lastToken) ? lastToken : null) ||
      (file.name && /[.][^.]+$/.test(file.name.toString()) ? file.name : null) ||
      lastToken ||
      file.name;

    const candidate = (preferred || 'File').toString();

    // Handle UUID-based filenames - extract extension for display
    const uuidPattern = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/i;
    if (uuidPattern.test(candidate)) {
      const matchExt = candidate.match(/\.[^.]+$/);
      return matchExt ? `File (${matchExt[0].slice(1).toUpperCase()})` : 'File';
    }
    
    // Handle item_<uuid> pattern
    const itemPattern = /^item_[a-f0-9-]+/i;
    if (itemPattern.test(candidate)) {
      const matchExt = candidate.match(/\.[^.]+$/);
      return matchExt ? `File (${matchExt[0].slice(1).toUpperCase()})` : 'File';
    }

    return candidate;
  };

  return (
    <div className="max-w-4xl mx-auto space-y-4 p-4">
      {/* Upload File Card */}
      <div className="tech-card p-4">
        <h3 className="tech-section-title mb-4 flex items-center gap-2">
          <svg className="w-4 h-4 text-tech-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          UPLOAD FILE
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-mono text-tech-textMuted mb-2">GROUP / CATEGORY (OPTIONAL)</label>
            <input
              type="text"
              value={newGroup}
              onChange={(e) => setNewGroup(e.target.value)}
              placeholder="e.g., Documents, Photos, Work"
              className="tech-input"
            />
          </div>
          <div className="border-2 border-dashed border-tech-border rounded-lg p-6 text-center hover:border-tech-accent/50 transition">
            <input
              type="file"
              onChange={handleFileUpload}
              disabled={uploading}
              className="hidden"
              id="file-upload-general"
            />
            <label htmlFor="file-upload-general" className="cursor-pointer">
              <svg className="w-12 h-12 text-tech-textMuted mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-tech-textMuted font-medium mb-1">
                {uploading ? 'UPLOADING...' : 'CLICK TO UPLOAD OR DROP FILE'}
              </p>
              <p className="text-tech-textDim text-xs">Any file type supported</p>
            </label>
          </div>
        </div>
      </div>

      {/* Files List */}
      {Object.keys(groupedFiles).length === 0 ? (
        <div className="tech-card p-6">
          <div className="text-center py-8">
            <svg className="w-12 h-12 text-tech-textDim mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <p className="text-tech-textDim text-sm">NO FILES UPLOADED</p>
          </div>
        </div>
      ) : (
        Object.keys(groupedFiles).map(group => (
          <div key={group} className="tech-card p-4">
            <h3 className="tech-section-title mb-4 flex items-center gap-2">
              <svg className="w-4 h-4 text-tech-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              </svg>
              {group.toUpperCase()}
              <span className="text-tech-textDim font-normal">({groupedFiles[group].length})</span>
            </h3>
            <div className="space-y-2">
              {groupedFiles[group].map((file) => (
                <div
                  key={file.id}
                  className="bg-tech-surface rounded-lg p-4 flex items-center justify-between hover:bg-tech-elevated transition"
                >
                  <button onClick={() => openViewer(file)} className="flex items-center gap-3 flex-1 text-left">
                    <div className="w-10 h-10 rounded bg-tech-accent/10 border border-tech-accent/30 flex items-center justify-center">
                      <svg className="w-5 h-5 text-tech-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-tech-text font-medium">{getDisplayName(file)}</p>
                      {typeof file.size === 'number' && (
                        <p className="text-tech-textDim text-xs font-mono">{formatFileSize(file.size)}</p>
                      )}
                    </div>
                  </button>
                  <button
                    onClick={() => handleRemoveFile(file.id)}
                    className="p-2 text-tech-textMuted hover:text-tech-danger hover:bg-tech-danger/10 rounded transition"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {viewer.open && (
        <div className="fixed inset-0 bg-tech-bg/80 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setViewer({ open: false, filename: '', content: '', saving: false, error: '' })}>
          <div className="tech-card max-w-3xl w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-tech-border flex items-center justify-between">
              <div className="text-tech-text font-mono font-semibold truncate">{viewer.filename}</div>
              <div className="flex items-center gap-2">
                <a
                  href={`/uploads/${encodeURIComponent(viewer.filename)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-1 bg-tech-surface rounded text-tech-textMuted hover:text-tech-text text-sm"
                >
                  OPEN RAW
                </a>
                <button
                  onClick={saveViewer}
                  disabled={viewer.saving}
                  className="tech-btn-primary py-1 text-xs disabled:opacity-50"
                >
                  {viewer.saving ? 'SAVING...' : 'SAVE'}
                </button>
              </div>
            </div>
            <div className="p-4">
              {viewer.error ? (
                <div className="text-tech-textMuted">{viewer.error}</div>
              ) : (
                <textarea
                  value={viewer.content}
                  onChange={(e) => setViewer(v => ({ ...v, content: e.target.value }))}
                  className="w-full h-96 bg-tech-bg text-tech-text border border-tech-border rounded-lg p-3 font-mono text-sm"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilesTab;
